/**
 * Admin V3 — Drivers — operational activation control (HIDE-RBAC)
 *
 * Toggles a driver's OPERATIONAL on/off state (`is_active`) via the EXISTING
 * backend endpoint PATCH /admin/drivers/{id}/activate  body { is_active }.
 * Migration of the legacy V1 "Activer / Désactiver" button (AdminDashboard.js:1194).
 *
 * SCOPE: this is an operational soft-enable/disable (is_active + availability
 * status), DISTINCT from compliance suspension (lifecycle_state, owned by
 * DriverSuspensionPanel) and from approval. The backend endpoint only sets
 * is_active + status and does NOT touch approval/lifecycle.
 *
 * RBAC: HIDE-gated on can('admin:drivers:detail') — the exact permission the
 * backend @rbac_permission enforces on the activate endpoint. No backend change.
 */

import React from 'react';
import { Power, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const ACTIVATION_PERMISSION = 'admin:drivers:detail';

const DriverActivationControl = ({ driver, onChange, locale = DEFAULT_LOCALE }) => {
  const { can } = usePermissions();
  const { showSuccess, showError } = useToast();

  const id = driver?.id || '_';
  const m = useAdminMutation(`/admin/drivers/${id}/activate`, 'patch');

  // Legacy convention: a driver is active unless is_active is explicitly false.
  const isActive = driver?.is_active !== false;
  const nextActive = !isActive;

  // Backend enforces admin:drivers:detail on the endpoint → fail-closed HIDE.
  if (!can(ACTIVATION_PERMISSION)) return null;

  const toggle = () => {
    m.mutate(
      { is_active: nextActive },
      {
        onSuccess: () => {
          showSuccess(t(nextActive ? 'drivers.activation.activated' : 'drivers.activation.deactivated', locale));
          if (onChange) onChange();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('drivers.activation.error', locale)),
      },
    );
  };

  return (
    <div className="flex flex-col items-end gap-1" data-testid={`driver-activation-${id}`}>
      <Button
        variant="outline"
        size="sm"
        disabled={m.loading}
        onClick={toggle}
        className={cnTone(isActive)}
        data-testid={isActive ? `driver-deactivate-${id}` : `driver-activate-${id}`}
      >
        {m.loading
          ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          : <Power className="h-3.5 w-3.5 mr-1.5" />}
        {t(isActive ? 'drivers.activation.deactivate' : 'drivers.activation.activate', locale)}
      </Button>
      {m.error && (
        <span className="text-[11px] text-rose-600" data-testid={`driver-activation-error-${id}`}>
          {t('drivers.activation.error', locale)}
        </span>
      )}
    </div>
  );
};

// Active drivers get a "deactivate" (amber) affordance; inactive get "activate" (emerald).
const cnTone = (isActive) =>
  isActive
    ? 'h-8 rounded-xl border-amber-200 bg-amber-50 px-3 text-xs font-semibold text-amber-700 hover:bg-amber-100'
    : 'h-8 rounded-xl border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700 hover:bg-emerald-100';

export { ACTIVATION_PERMISSION };
export default DriverActivationControl;
